import { Component, Inject } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ControlsOf, FormControl, FormGroup } from '@ngneat/reactive-forms';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog-service/confirm-dialog.service';
import { Person, PersonService } from 'src/app/services/person.service';

const PHONE_NUMBER_PATTERN = /^\+?[0-9 ()-]{7,15}$/;

export interface CreatePersonDialogData {
    afas_customer_id: string;
    last_name?: string;
    person?: Person;
}

interface CreatePersonForm {
    first_name: string;
    last_name: string;
    phone_number: string;
    email: string;
}

@Component({
    selector: 'app-create-person-dialog',
    templateUrl: './create-person-dialog.component.html',
    styleUrls: ['./create-person-dialog.component.scss'],
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
    ],
})
export class CreatePersonDialogComponent {
    form = new FormGroup<ControlsOf<CreatePersonForm>>({
        first_name: new FormControl('', Validators.required),
        last_name: new FormControl('', Validators.required),
        phone_number: new FormControl('', Validators.pattern(PHONE_NUMBER_PATTERN)),
        email: new FormControl('', Validators.email),
    });

    saving = false;
    isEditMode = false;

    constructor(
        public dialogRef: MatDialogRef<CreatePersonDialogComponent, Person>,
        @Inject(MAT_DIALOG_DATA) public data: CreatePersonDialogData,
        private personService: PersonService,
        private confirmDialog: ConfirmDialogService
    ) {
        if (data.person) {
            this.isEditMode = true;
            const [firstName, ...rest] = data.person.name.split(' ');
            this.form.controls.first_name.setValue(firstName ?? '');
            this.form.controls.last_name.setValue(rest.join(' '));
            this.form.controls.phone_number.setValue(data.person.phone_number ?? '');
            this.form.controls.email.setValue(data.person.email ?? '');
        } else {
            this.form.controls.last_name.setValue(data.last_name ?? '');
        }
    }

    save() {
        if (!this.form.valid) {
            Object.keys(this.form.controls).forEach(key => {
                this.form.controls[key].markAsTouched();
            });
            return;
        }

        this.saving = true;

        const payload = {
            first_name: this.form.value.first_name,
            last_name: this.form.value.last_name,
            phone_number: this.form.value.phone_number || null,
            email: this.form.value.email || null,
        };

        const request = this.isEditMode
            ? this.personService.update(this.data.person.id, payload)
            : this.personService.create({ ...payload, afas_customer_id: this.data.afas_customer_id });

        request.subscribe({
            next: response => {
                this.saving = false;
                this.dialogRef.close(response.data);
            },
            error: () => {
                this.saving = false;
                this.confirmDialog.confirm({
                    title: 'Fout tijdens het opslaan',
                    text: 'Er is iets fout gegaan bij het opslaan van de contactpersoon',
                    confirm: 'Oké',
                    cancel: null,
                });
            },
        });
    }

    cancel() {
        this.dialogRef.close();
    }
}
